import { t } from '../i18n/i18n';

export interface ShareModalOptions {
  title?: string;
  desc?: string;
  suffix?: string;
}

export class ShareModal {
  private element: HTMLElement;
  private customTitle: string | null = null;
  private customDesc: string | null = null;
  private customSuffix: string | null = null;

  private titleEl!: HTMLElement;
  private descEl!: HTMLElement;
  private tweetBodyEl!: HTMLElement;
  private tweetTagsEl!: HTMLElement;
  private btnPostXEl!: HTMLButtonElement;
  private btnCopyLinkEl!: HTMLButtonElement;
  private btnCloseEl!: HTMLButtonElement;
  private toastEl!: HTMLElement;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop';
    this.element.id = 'modalShare';
    this.render();
    this.initElements();
    this.bindEvents();
  }

  getElement(): HTMLElement {
    return this.element;
  }

  show(options?: ShareModalOptions): void {
    this.customTitle = options?.title || null;
    this.customDesc = options?.desc || null;
    this.customSuffix = options?.suffix || null;

    this.updateTexts();
    this.element.classList.add('show');
  }

  hide(): void {
    this.element.classList.remove('show');
    if (this.toastEl) {
      this.toastEl.classList.remove('show');
    }
  }

  private getShareUrl(): string {
    if (typeof window !== 'undefined' && window.location.href && !window.location.href.startsWith('about:')) {
      return window.location.origin || window.location.href;
    }
    return 'https://jigglytouch.com';
  }

  private render(): void {
    const texts = t().share;
    const appName = t().app.title;
    const shareUrl = this.getShareUrl();

    this.element.innerHTML = `
      <div class="modal-card" style="max-width: 480px;">
        <div class="modal-header">
          <div class="modal-title">
            <span style="font-weight: 800; font-size: 16px;">𝕏</span>
            <span id="shareModalTitle">${texts.modalTitle}</span>
          </div>
          <button class="modal-close" id="btnCloseShareX">&times;</button>
        </div>
        <div class="modal-body" style="text-align: center;">
          <p id="shareModalDesc" style="font-size: 13px; color: var(--color-text-muted); line-height: 1.5; margin-bottom: 12px;">
            ${texts.modalDesc}
          </p>

          <!-- 𝕏 ツイートプレビュー風カード -->
          <div class="tweet-preview-card">
            <div class="tweet-preview-header">
              <div class="tweet-preview-avatar">🍮</div>
              <div>
                <div class="tweet-preview-author">${appName}</div>
                <div class="tweet-preview-handle">@PurinTouch</div>
              </div>
            </div>
            <div class="tweet-preview-body" id="shareTweetBody">
              ${texts.tweetText}
            </div>
            <div style="font-size: 12px; color: #1d9bf0; margin-top: 4px; word-break: break-all;">
              ${shareUrl}
            </div>
            <div class="tweet-preview-tags" id="shareTweetTags">
              #${texts.hashtags.split(',').join(' #')}
            </div>
          </div>

          <div style="display: flex; gap: 8px; justify-content: center; margin-top: 14px;">
            <button class="btn btn-x-share" id="btnPostToX" style="flex: 1.2; padding: 10px 18px; font-size: 13px;">
              <span>𝕏</span> <span id="btnPostToXText">${texts.postBtn}</span>
            </button>
            <button class="btn btn-secondary" id="btnCopyShareLink" style="flex: 1; padding: 10px 14px; font-size: 13px;">
              ${texts.copyBtn}
            </button>
          </div>

          <div>
            <div class="share-copy-toast" id="shareCopyToast">
              ✓ ${texts.copiedNotice}
            </div>
          </div>
        </div>
        <div class="modal-footer" style="display: flex; justify-content: flex-end;">
          <button class="btn btn-secondary" id="btnShareModalClose">${texts.closeBtn}</button>
        </div>
      </div>
    `;
  }

  private initElements(): void {
    this.titleEl = this.element.querySelector('#shareModalTitle') as HTMLElement;
    this.descEl = this.element.querySelector('#shareModalDesc') as HTMLElement;
    this.tweetBodyEl = this.element.querySelector('#shareTweetBody') as HTMLElement;
    this.tweetTagsEl = this.element.querySelector('#shareTweetTags') as HTMLElement;
    this.btnPostXEl = this.element.querySelector('#btnPostToX') as HTMLButtonElement;
    this.btnCopyLinkEl = this.element.querySelector('#btnCopyShareLink') as HTMLButtonElement;
    this.btnCloseEl = this.element.querySelector('#btnShareModalClose') as HTMLButtonElement;
    this.toastEl = this.element.querySelector('#shareCopyToast') as HTMLElement;
  }

  private bindEvents(): void {
    this.element.querySelector('#btnCloseShareX')?.addEventListener('click', () => this.hide());
    this.btnCloseEl?.addEventListener('click', () => this.hide());

    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) {
        this.hide();
      }
    });

    // 𝕏 (Twitter) 投稿ボタン
    this.btnPostXEl?.addEventListener('click', () => {
      this.openTwitterIntent();
    });

    // リンクコピーボタン
    this.btnCopyLinkEl?.addEventListener('click', async () => {
      await this.copyShareLink();
    });
  }

  private openTwitterIntent(): void {
    const texts = t().share;
    const url = this.getShareUrl();
    const body = this.customSuffix ? `${texts.tweetText} ${this.customSuffix}` : texts.tweetText;
    const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(body)}&url=${encodeURIComponent(url)}&hashtags=${encodeURIComponent(texts.hashtags)}`;

    if (typeof window !== 'undefined') {
      const width = 580;
      const height = 480;
      const left = Math.max(0, (window.screen.width - width) / 2);
      const top = Math.max(0, (window.screen.height - height) / 2);
      window.open(intentUrl, '_blank', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
    }
  }

  private async copyShareLink(): Promise<void> {
    const texts = t().share;
    const url = this.getShareUrl();
    const fullText = `${texts.tweetText}\n${url}\n#${texts.hashtags.split(',').join(' #')}`;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullText);
      } else {
        // フォールバック
        const ta = document.createElement('textarea');
        ta.value = fullText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      if (this.toastEl) {
        this.toastEl.classList.add('show');
        setTimeout(() => {
          this.toastEl.classList.remove('show');
        }, 2500);
      }
    } catch {
      // ignore
    }
  }

  updateTexts(): void {
    const texts = t().share;

    if (this.titleEl) {
      this.titleEl.textContent = this.customTitle || texts.modalTitle;
    }
    if (this.descEl) {
      this.descEl.textContent = this.customDesc || texts.modalDesc;
    }
    if (this.tweetBodyEl) {
      this.tweetBodyEl.textContent = this.customSuffix ? `${texts.tweetText} ${this.customSuffix}` : texts.tweetText;
    }
    if (this.tweetTagsEl) {
      this.tweetTagsEl.textContent = `#${texts.hashtags.split(',').join(' #')}`;
    }
    if (this.btnPostXEl) {
      const textSpan = this.btnPostXEl.querySelector('#btnPostToXText');
      if (textSpan) textSpan.textContent = texts.postBtn;
    }
    if (this.btnCopyLinkEl) {
      this.btnCopyLinkEl.textContent = texts.copyBtn;
    }
    if (this.btnCloseEl) {
      this.btnCloseEl.textContent = texts.closeBtn;
    }
    if (this.toastEl) {
      this.toastEl.textContent = `✓ ${texts.copiedNotice}`;
    }
  }
}
